import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShoppingCart, Plus, Minus, ChevronLeft, Store, Bike, Loader2, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const ORANGE = '#D44E20';

export default function OrderPage({ embedded = false, presetToken, presetPin, presetCustomerId, presetName, presetPoints } = {}) {
  const [ctx, setCtx] = useState(null);
  const [menu, setMenu] = useState({ categories: [], items: [], modifier_groups: [] });
  const [cart, setCart] = useState([]); // {key,item_id,name,price,quantity,modifiers:[{name,price_delta}]}
  const [step, setStep] = useState('menu'); // menu | identity | details
  const [loading, setLoading] = useState(true);
  const [cartOpen, setCartOpen] = useState(false);

  // identity
  const [auth, setAuth] = useState(() => embedded
    ? { token: presetToken, pin: presetPin, customerId: presetCustomerId, name: presetName || '', points: presetPoints || 0 }
    : null);
  const [mode, setMode] = useState('login'); // login | register
  const [f, setF] = useState({ phone: '', pin: '', name: '', email: '', birthday: '', marketing_consent: false, gdpr: false });
  const [busy, setBusy] = useState(false);

  // details
  const [orderType, setOrderType] = useState('takeaway');
  const [addr, setAddr] = useState({ street: '', city: '', zip: '', phone: '', intercom: '', notes: '' });
  const [quote, setQuote] = useState(null); // {covered,fee_eur,eta_min,zone_label,reason}
  const [quoting, setQuoting] = useState(false);
  const [pickupTime, setPickupTime] = useState('');
  const [payment, setPayment] = useState('stripe');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const c = (await axios.get(`${API}/api/public/order-context`)).data;
        setCtx(c);
        const m = (await axios.get(`${API}/api/public/menu/${c.brand_id}`)).data;
        setMenu(m);
      } catch (e) {
        toast.error('Impossibile caricare il menu');
      } finally { setLoading(false); }
    })();
  }, []);

  const cartTotal = useMemo(() => cart.reduce((s, l) => s + (l.price + (l.modifiers || []).reduce((a, mo) => a + (mo.price_delta || 0), 0)) * l.quantity, 0), [cart]);
  const cartCount = useMemo(() => cart.reduce((s, l) => s + l.quantity, 0), [cart]);
  const itemsByCat = useMemo(() => {
    const map = {};
    (menu.items || []).forEach(it => { (map[it.category_id] = map[it.category_id] || []).push(it); });
    return map;
  }, [menu]);

  // Cross-sell / up-sell: prefer real "bought together" data from the backend;
  // fall back to a local heuristic (flagged → complementary categories → cheap).
  const localSuggestions = useMemo(() => {
    if (cart.length === 0) return [];
    const inCart = new Set(cart.map(l => l.item_id));
    const all = (menu.items || []).filter(it => !inCart.has(it.id));
    const KW = /bevand|drink|bibit|dolc|dessert|extra|contorn|salsa|sauce|side|caff|birr/i;
    const catName = (id) => (menu.categories || []).find(c => c.id === id)?.name || '';
    const flagged = all.filter(it => it.is_suggested);
    const complementary = all.filter(it => !it.is_suggested && KW.test(catName(it.category_id)));
    const cheap = all.filter(it => !it.is_suggested && !KW.test(catName(it.category_id))).sort((a, b) => a.price - b.price);
    const seen = new Set();
    const out = [];
    for (const it of [...flagged, ...complementary, ...cheap]) {
      if (seen.has(it.id)) continue;
      seen.add(it.id); out.push(it);
      if (out.length >= 6) break;
    }
    return out;
  }, [menu, cart]);

  const [smartSuggestions, setSmartSuggestions] = useState([]);
  const cartIdsKey = useMemo(() => cart.map(l => l.item_id).sort().join(','), [cart]);
  useEffect(() => {
    if (!ctx?.brand_id || cart.length === 0) { setSmartSuggestions([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = (await axios.get(`${API}/api/public/menu/${ctx.brand_id}/suggestions`, {
          params: { item_ids: cartIdsKey, limit: 6 },
        })).data;
        if (!cancelled) setSmartSuggestions(r.suggestions || []);
      } catch { if (!cancelled) setSmartSuggestions([]); }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [ctx, cartIdsKey, cart.length]);

  const suggestions = smartSuggestions.length > 0 ? smartSuggestions : localSuggestions;

  const REASON_LABELS = { bought_together: 'Spesso insieme', suggested: 'Consigliato', popular: 'Popolare' };
  const Suggestions = ({ compact }) => {
    if (suggestions.length === 0) return null;
    return (
      <div data-testid="cross-sell" className={compact ? 'mt-4' : 'bg-white rounded-xl p-4'}>
        <p className="text-sm font-bold text-slate-800 mb-2">Aggiungi anche… 🌶️</p>
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          {suggestions.map(it => (
            <div key={it.id} data-testid={`suggestion-${it.id}`} className="flex-shrink-0 w-32 border border-slate-100 rounded-xl p-2 bg-slate-50">
              {it.image_url && <img src={it.image_url} alt="" className="w-full h-16 object-cover rounded-lg mb-1" />}
              {REASON_LABELS[it.reason] && (
                <span data-testid={`suggestion-reason-${it.id}`} className="inline-block text-[9px] font-semibold px-1.5 py-0.5 rounded-full mb-1" style={{ backgroundColor: '#FFF0EA', color: ORANGE }}>
                  {it.reason === 'bought_together' ? '🔥 ' : ''}{REASON_LABELS[it.reason]}
                </span>
              )}
              <p className="text-xs font-semibold text-slate-800 line-clamp-2 leading-tight h-8">{it.name}</p>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs font-bold" style={{ color: ORANGE }}>{Number(it.price).toFixed(2)}€</span>
                <button onClick={() => addToCart(it)} data-testid={`suggestion-add-${it.id}`} className="rounded-full w-7 h-7 flex items-center justify-center text-white" style={{ backgroundColor: ORANGE }}><Plus size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const addToCart = (it) => {
    const key = it.id;
    setCart(prev => {
      const found = prev.find(l => l.key === key);
      if (found) return prev.map(l => l.key === key ? { ...l, quantity: l.quantity + 1 } : l);
      return [...prev, { key, item_id: it.id, name: it.name, price: it.price, quantity: 1, modifiers: [] }];
    });
    toast.success(`${it.name} aggiunto`, { position: 'top-center' });
  };
  const changeQty = (key, d) => setCart(prev => prev.flatMap(l => {
    if (l.key !== key) return [l];
    const q = l.quantity + d;
    return q <= 0 ? [] : [{ ...l, quantity: q }];
  }));

  const fetchCustomer = async (token, pin) => {
    const d = (await axios.get(`${API}/api/my/${token}/data`, { params: pin ? { pin } : {} })).data;
    const cust = d.customer || d;
    return { token, pin, customerId: cust.id || cust._id || cust.customer_id, name: cust.name || '', points: cust.points || 0 };
  };

  const doLogin = async () => {
    if (!f.phone || f.pin.length !== 4) { toast.error('Inserisci telefono e PIN a 4 cifre'); return; }
    setBusy(true);
    try {
      const r = (await axios.post(`${API}/api/public/customer-lookup`, { phone: f.phone, pin: f.pin })).data;
      const token = r.token || r.dashboard_token;
      if (!token) throw new Error('no token');
      const a = await fetchCustomer(token, f.pin);
      setAuth(a); setStep('details');
      toast.success(`Bentornato ${a.name || ''}!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Credenziali non valide');
    } finally { setBusy(false); }
  };

  const doRegister = async () => {
    if (!f.name || !f.phone) { toast.error('Nome e telefono obbligatori'); return; }
    if (!f.gdpr) { toast.error('Devi accettare i termini per creare l\u2019account e ordinare'); return; }
    setBusy(true);
    try {
      const r = (await axios.post(`${API}/api/public/register/${ctx.business_id}`, {
        name: f.name, phone: f.phone, email: f.email || null, birthday: f.birthday || null,
        marketing_consent: f.marketing_consent, third_party_consent: false, acquisition_channel: 'ordine_online',
      })).data;
      const a = await fetchCustomer(r.dashboard_token, r.dashboard_pin);
      setAuth(a); setStep('details');
      toast.success('Account creato! Ti abbiamo inviato le credenziali via SMS.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Registrazione non riuscita');
    } finally { setBusy(false); }
  };

  const doRecovery = async () => {
    if (!f.phone) { toast.error('Inserisci il tuo numero di telefono'); return; }
    try {
      await axios.post(`${API}/api/public/customer-recovery`, { phone: f.phone });
      toast.success('Se il numero è registrato, ti abbiamo inviato le credenziali via SMS.');
    } catch { toast.success('Se il numero è registrato, ti abbiamo inviato le credenziali via SMS.'); }
  };

  const getQuote = async () => {
    if (!addr.street || !addr.city || !addr.zip) { toast.error('Completa via, città e CAP'); return; }
    setQuoting(true);
    try {
      const q = (await axios.post(`${API}/api/my/${auth.token}/delivery-quote`, {
        street: addr.street, city: addr.city, zip: addr.zip, cart_total_eur: cartTotal,
      })).data;
      setQuote(q);
      if (!q.covered) toast.error(q.reason || 'Consegna non disponibile');
    } catch (e) {
      toast.error('Impossibile calcolare la consegna');
    } finally { setQuoting(false); }
  };

  const payMethods = orderType === 'delivery'
    ? [['stripe', 'Carta'], ['paypal', 'PayPal'], ['points', 'Punti fedeltà']]
    : [['stripe', 'Carta'], ['paypal', 'PayPal'], ['points', 'Punti fedeltà'], ['cod', 'Contanti alla consegna/ritiro']];

  const submit = async () => {
    if (orderType === 'delivery') {
      if (!addr.phone) { toast.error('Telefono obbligatorio per la consegna'); return; }
      if (!quote || !quote.covered) { toast.error('Calcola prima la consegna per il tuo indirizzo'); return; }
    }
    setBusy(true);
    const body = {
      items: cart.map(l => ({ item_id: l.item_id, name: l.name, price: l.price, quantity: l.quantity, modifiers: l.modifiers })),
      payment_method: payment, order_type: orderType, notes,
      pickup_time: orderType === 'takeaway' ? (pickupTime || null) : null,
      delivery_address: orderType === 'delivery' ? { ...addr } : null,
      origin_url: window.location.origin,
    };
    try {
      if (payment === 'paypal') {
        const r = (await axios.post(`${API}/api/orders/checkout/paypal`, body, { headers: { 'X-Customer-Id': auth.customerId } })).data;
        const url = r.approve_url || r.url;
        if (url) { window.location.href = url; return; }
        throw new Error('no approve url');
      }
      const r = (await axios.post(`${API}/api/orders/checkout`, body, { headers: { 'X-Customer-Id': auth.customerId } })).data;
      const url = r.checkout_url || r.url;
      if (payment === 'stripe' && url) { window.location.href = url; return; }
      // points / cod → order created immediately → go to live tracking
      toast.success('Ordine confermato! 🎉');
      setCart([]);
      const newOrderId = r.order?.id || r.order?._id || r.order_id;
      if (newOrderId) { window.location.href = `/my/order/${newOrderId}`; return; }
      if (embedded) { setStep('menu'); } else { window.location.href = `/my/${auth.token}`; }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore durante il checkout');
    } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" style={{ color: ORANGE }} /></div>;

  const logo = ctx?.branding?.logo_url;

  return (
    <div className={embedded ? 'bg-slate-50' : 'min-h-screen bg-slate-50'} data-testid="order-page">
      <header className={`bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between z-20 ${embedded ? '' : 'sticky top-0'}`}>
        <div className="flex items-center gap-2">
          {step !== 'menu' && (
            <button onClick={() => setStep(step === 'details' ? (embedded ? 'menu' : 'identity') : 'menu')} data-testid="order-back-btn" className="p-1"><ChevronLeft size={22} /></button>
          )}
          {embedded ? <span className="font-bold text-slate-700 text-sm">Ordina</span> : (logo ? <img src={logo} alt="" className="h-8" /> : <span className="font-extrabold" style={{ color: ORANGE }}>{ctx?.business_name}</span>)}
        </div>
        {step === 'menu' && (
          <button onClick={() => setCartOpen(true)} data-testid="open-cart-btn" className="relative rounded-full px-4 py-2 text-white text-sm font-bold flex items-center gap-2" style={{ backgroundColor: ORANGE }}>            <ShoppingCart size={16} /> {cartTotal.toFixed(2)}€
            {cartCount > 0 && <span className="absolute -top-1 -right-1 bg-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center" style={{ color: ORANGE }}>{cartCount}</span>}
          </button>
        )}
      </header>

      {step === 'menu' && (
        <div className="max-w-2xl mx-auto p-4 pb-28">
          {(menu.categories || []).map(cat => (
            <div key={cat.id} className="mb-6" data-testid={`menu-category-${cat.id}`}>
              <h2 className="text-lg font-bold text-slate-900 mb-3">{cat.name}</h2>
              <div className="space-y-2">
                {(itemsByCat[cat.id] || []).map(it => {
                  const unavailAll = it.sold_out_scope === 'all' && (it.sold_out_indefinite || (it.sold_out_until && new Date(it.sold_out_until) > new Date()));
                  return (
                  <div key={it.id} data-testid={`menu-item-${it.id}`} className={`bg-white rounded-xl p-3 flex items-center justify-between shadow-sm ${unavailAll ? 'opacity-60' : ''}`}>
                    <div className="flex-1 pr-3">
                      <p className="font-semibold text-slate-900 text-sm">{it.name} {unavailAll && <span className="text-[10px] font-bold text-red-500 align-middle">· Non disponibile</span>}</p>
                      {it.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{it.description}</p>}
                      <p className="text-sm font-bold mt-1" style={{ color: ORANGE }}>{Number(it.price).toFixed(2)}€</p>
                    </div>
                    <button onClick={() => addToCart(it)} disabled={unavailAll} data-testid={`add-item-${it.id}`} className="rounded-full w-9 h-9 flex items-center justify-center text-white flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed" style={{ backgroundColor: ORANGE }}><Plus size={18} /></button>
                  </div>
                  );
                })}
              </div>
            </div>
          ))}
          {cartCount > 0 && (
            <div className={embedded ? 'mt-4' : 'fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-slate-100 z-20'}>
              <button onClick={() => setStep(auth ? 'details' : 'identity')} data-testid="checkout-btn" className="w-full max-w-2xl mx-auto block rounded-xl py-3 text-white font-bold" style={{ backgroundColor: ORANGE }}>
                Procedi al checkout · {cartTotal.toFixed(2)}€
              </button>
            </div>
          )}
        </div>
      )}

      {cartOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 flex justify-end" onClick={() => setCartOpen(false)}>
          <div className="bg-white w-full max-w-sm h-full p-4 overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="cart-drawer">
            <div className="flex items-center justify-between mb-4"><h3 className="font-bold">Il tuo ordine</h3><button onClick={() => setCartOpen(false)}><X size={20} /></button></div>
            {cart.length === 0 ? <p className="text-slate-400 text-sm">Carrello vuoto</p> : cart.map(l => (
              <div key={l.key} className="flex items-center justify-between py-2 border-b border-slate-100">
                <div className="flex-1"><p className="text-sm font-medium">{l.name}</p><p className="text-xs text-slate-500">{(l.price * l.quantity).toFixed(2)}€</p></div>
                <div className="flex items-center gap-2">
                  <button onClick={() => changeQty(l.key, -1)} className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center"><Minus size={14} /></button>
                  <span className="w-5 text-center text-sm font-bold">{l.quantity}</span>
                  <button onClick={() => changeQty(l.key, 1)} className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center"><Plus size={14} /></button>
                </div>
              </div>
            ))}
            <div className="mt-4 flex justify-between font-bold"><span>Totale</span><span>{cartTotal.toFixed(2)}€</span></div>
            {cart.length > 0 && <Suggestions compact />}
          </div>
        </div>
      )}

      {step === 'identity' && (
        <div className="max-w-md mx-auto p-4" data-testid="identity-step">
          <div className="flex gap-2 mb-4 bg-white rounded-xl p-1">
            <button onClick={() => setMode('login')} data-testid="tab-login" className={`flex-1 py-2 rounded-lg text-sm font-semibold ${mode === 'login' ? 'text-white' : 'text-slate-500'}`} style={mode === 'login' ? { backgroundColor: ORANGE } : {}}>Sono già iscritto</button>
            <button onClick={() => setMode('register')} data-testid="tab-register" className={`flex-1 py-2 rounded-lg text-sm font-semibold ${mode === 'register' ? 'text-white' : 'text-slate-500'}`} style={mode === 'register' ? { backgroundColor: ORANGE } : {}}>Nuovo cliente</button>
          </div>
          {mode === 'login' ? (
            <div className="bg-white rounded-xl p-4 space-y-3">
              <p className="text-sm text-slate-600">Sei già iscritto a {ctx?.business_name}? Accedi con telefono e PIN.</p>
              <input data-testid="login-phone" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} placeholder="Telefono" className="w-full border rounded-lg px-3 py-2.5 text-sm" />
              <input data-testid="login-pin" value={f.pin} onChange={e => setF({ ...f, pin: e.target.value.replace(/\D/g, '').slice(0, 4) })} placeholder="PIN (4 cifre)" inputMode="numeric" className="w-full border rounded-lg px-3 py-2.5 text-sm" />
              <button onClick={doLogin} disabled={busy} data-testid="login-submit" className="w-full rounded-lg py-2.5 text-white font-bold" style={{ backgroundColor: ORANGE }}>{busy ? '...' : 'Accedi e continua'}</button>
              <button onClick={doRecovery} data-testid="recovery-btn" className="w-full text-xs text-slate-500 underline">Ho dimenticato il PIN — inviami le credenziali via SMS</button>
            </div>
          ) : (
            <div className="bg-white rounded-xl p-4 space-y-3">
              <input data-testid="reg-name" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Nome e cognome *" className="w-full border rounded-lg px-3 py-2.5 text-sm" />
              <input data-testid="reg-phone" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} placeholder="Telefono *" className="w-full border rounded-lg px-3 py-2.5 text-sm" />
              <input data-testid="reg-email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} placeholder="Email (opzionale)" className="w-full border rounded-lg px-3 py-2.5 text-sm" />
              <label className="flex items-start gap-2 text-xs text-slate-600">
                <input type="checkbox" data-testid="reg-gdpr" checked={f.gdpr} onChange={e => setF({ ...f, gdpr: e.target.checked })} className="mt-0.5" />
                <span>Accetto i termini e la privacy policy e acconsento alla creazione del mio account {ctx?.business_name}. *</span>
              </label>
              <label className="flex items-start gap-2 text-xs text-slate-600">
                <input type="checkbox" data-testid="reg-marketing" checked={f.marketing_consent} onChange={e => setF({ ...f, marketing_consent: e.target.checked })} className="mt-0.5" />
                <span>Voglio ricevere offerte e novità (facoltativo).</span>
              </label>
              <button onClick={doRegister} disabled={busy} data-testid="register-submit" className="w-full rounded-lg py-2.5 text-white font-bold" style={{ backgroundColor: ORANGE }}>{busy ? '...' : 'Crea account e continua'}</button>
            </div>
          )}
        </div>
      )}

      {step === 'details' && auth && (
        <div className="max-w-md mx-auto p-4 space-y-4" data-testid="details-step">
          <div className="bg-white rounded-xl p-3 text-sm">Ciao <b>{auth.name}</b> · {auth.points} punti</div>

          {ctx?.orders_paused && (
            <div data-testid="orders-paused-banner" className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm font-semibold">🚫 La cucina non accetta ordini in questo momento. Riprova tra poco.</div>
          )}
          {!ctx?.orders_paused && ctx?.busy && (
            <div data-testid="busy-banner" className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-3 text-sm">🔥 Cucina molto impegnata. Attesa stimata attuale: <b>~{ctx.current_wait_min} min</b>.</div>
          )}
          {!ctx?.orders_paused && !ctx?.busy && ctx?.current_wait_min != null && (
            <div data-testid="wait-banner" className="bg-slate-50 border border-slate-200 text-slate-600 rounded-xl p-2.5 text-xs">⏱ Attesa stimata attuale: <b>~{ctx.current_wait_min} min</b></div>
          )}

          <div className="bg-white rounded-xl p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Tipo di ordine</p>
            <div className="grid grid-cols-2 gap-2">
              <button disabled={ctx && ctx.takeaway_open === false} onClick={() => { setOrderType('takeaway'); }} data-testid="type-takeaway" className={`rounded-lg py-3 flex flex-col items-center gap-1 border disabled:opacity-40 disabled:cursor-not-allowed ${orderType === 'takeaway' ? 'border-2' : 'border-slate-200'}`} style={orderType === 'takeaway' ? { borderColor: ORANGE, color: ORANGE } : {}}><Store size={18} /><span className="text-xs font-semibold">Ritiro (Take-away)</span>{ctx && ctx.takeaway_open === false && <span className="text-[10px] text-red-500">Chiuso ora</span>}</button>
              {ctx?.delivery_enabled && (
                <button disabled={ctx && ctx.delivery_open === false} onClick={() => { setOrderType('delivery'); if (payment === 'cod') setPayment('stripe'); }} data-testid="type-delivery" className={`rounded-lg py-3 flex flex-col items-center gap-1 border disabled:opacity-40 disabled:cursor-not-allowed ${orderType === 'delivery' ? 'border-2' : 'border-slate-200'}`} style={orderType === 'delivery' ? { borderColor: ORANGE, color: ORANGE } : {}}><Bike size={18} /><span className="text-xs font-semibold">Consegna</span>{ctx && ctx.delivery_open === false && <span className="text-[10px] text-red-500">Chiuso ora</span>}</button>
              )}
            </div>
          </div>

          {orderType === 'takeaway' && (
            <div className="bg-white rounded-xl p-4">
              <label className="text-xs font-semibold text-slate-500 uppercase">Orario di ritiro (opzionale)</label>
              <input data-testid="pickup-time" value={pickupTime} onChange={e => setPickupTime(e.target.value)} placeholder="Es. 20:30" className="w-full border rounded-lg px-3 py-2.5 text-sm mt-1" />
            </div>
          )}

          {orderType === 'delivery' && (
            <div className="bg-white rounded-xl p-4 space-y-2">
              <input data-testid="addr-street" value={addr.street} onChange={e => setAddr({ ...addr, street: e.target.value })} placeholder="Via e numero civico *" className="w-full border rounded-lg px-3 py-2.5 text-sm" />
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="addr-city" value={addr.city} onChange={e => setAddr({ ...addr, city: e.target.value })} placeholder="Città *" className="border rounded-lg px-3 py-2.5 text-sm" />
                <input data-testid="addr-zip" value={addr.zip} onChange={e => setAddr({ ...addr, zip: e.target.value })} placeholder="CAP *" className="border rounded-lg px-3 py-2.5 text-sm" />
              </div>
              <input data-testid="addr-phone" value={addr.phone} onChange={e => setAddr({ ...addr, phone: e.target.value })} placeholder="Telefono *" className="w-full border rounded-lg px-3 py-2.5 text-sm" />
              <input data-testid="addr-intercom" value={addr.intercom} onChange={e => setAddr({ ...addr, intercom: e.target.value })} placeholder="Citofono / interno (opzionale)" className="w-full border rounded-lg px-3 py-2.5 text-sm" />
              <button onClick={getQuote} disabled={quoting} data-testid="quote-btn" className="w-full rounded-lg py-2 text-sm font-semibold border" style={{ borderColor: ORANGE, color: ORANGE }}>{quoting ? 'Calcolo...' : 'Calcola consegna'}</button>
              {quote && quote.covered && <div data-testid="quote-result" className="text-xs bg-green-50 text-green-700 rounded-lg p-2">Consegna {Number(quote.fee_eur).toFixed(2)}€ · ~{quote.eta_min} min {quote.zone_label ? `· ${quote.zone_label}` : ''}</div>}
              {quote && !quote.covered && <div data-testid="quote-result" className="text-xs bg-red-50 text-red-600 rounded-lg p-2">{quote.reason || 'Consegna non disponibile'}</div>}
            </div>
          )}

          <div className="bg-white rounded-xl p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Pagamento</p>
            <div className="space-y-2">
              {payMethods.map(([val, label]) => (
                <label key={val} data-testid={`pay-${val}`} className={`flex items-center gap-2 border rounded-lg px-3 py-2.5 text-sm cursor-pointer ${payment === val ? 'border-2' : 'border-slate-200'}`} style={payment === val ? { borderColor: ORANGE } : {}}>
                  <input type="radio" name="pay" checked={payment === val} onChange={() => setPayment(val)} />
                  <span>{label}{val === 'points' ? ` (hai ${auth.points} punti)` : ''}</span>
                </label>
              ))}
            </div>
            {orderType === 'delivery' && <p className="text-[11px] text-slate-400 mt-2">Le consegne sono a pagamento anticipato (no contrassegno).</p>}
          </div>

          <textarea data-testid="order-notes" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Note per il locale (opzionale)" rows={2} className="w-full border rounded-lg px-3 py-2.5 text-sm" />

          <Suggestions />

          <div className="bg-white rounded-xl p-4 text-sm space-y-1">
            <div className="flex justify-between"><span>Subtotale</span><span>{cartTotal.toFixed(2)}€</span></div>
            {orderType === 'delivery' && quote?.covered && <div className="flex justify-between"><span>Consegna</span><span>{Number(quote.fee_eur).toFixed(2)}€</span></div>}
            <div className="flex justify-between font-bold text-base pt-1"><span>Totale</span><span>{(cartTotal + (orderType === 'delivery' && quote?.covered ? Number(quote.fee_eur) : 0)).toFixed(2)}€</span></div>
          </div>

          <button onClick={submit} disabled={busy || ctx?.orders_paused} data-testid="place-order-btn" className="w-full rounded-xl py-3 text-white font-bold disabled:opacity-50" style={{ backgroundColor: ORANGE }}>{busy ? 'Attendere...' : (ctx?.orders_paused ? 'Ordini in pausa' : 'Conferma e paga')}</button>
        </div>
      )}
    </div>
  );
}
